import Phaser from 'phaser';
import { Upgrade } from '../systems/UpgradeSystem';

interface UpgradeSceneData {
  upgrades: Upgrade[];
}

export class UpgradeScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UpgradeScene' });
  }

  create(data: UpgradeSceneData) {
    const { width, height } = this.cameras.main;
    const { upgrades } = data;

    // Full-screen dark overlay so the frozen game behind is visible but dimmed
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.82);

    // Animated "LEVEL UP!" header
    const levelText = this.add.text(width / 2, 95, '⬆  LEVEL UP!', {
      fontSize: '54px',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#cc5500',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(10);

    this.tweens.add({
      targets: levelText,
      scaleX: 1.06,
      scaleY: 1.06,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });

    this.add.text(width / 2, 162, 'Choose an upgrade', {
      fontSize: '21px',
      color: '#aaaaaa',
    }).setOrigin(0.5).setDepth(10);

    // Card layout — evenly spaced across the screen
    const cardWidth = 272;
    const cardHeight = 310;
    const gap = 44;
    const totalW = upgrades.length * cardWidth + (upgrades.length - 1) * gap;
    const firstCardCX = (width - totalW) / 2 + cardWidth / 2;
    const cardCY = height / 2 + 30;

    upgrades.forEach((upgrade, i) => {
      const cx = firstCardCX + i * (cardWidth + gap);
      this.buildCard(cx, cardCY, cardWidth, cardHeight, upgrade, i);
    });
  }

  private buildCard(
    cx: number,
    cy: number,
    w: number,
    h: number,
    upgrade: Upgrade,
    index: number,
  ) {
    // Container slides in from below and fades in
    const container = this.add.container(cx, cy + 32);
    container.setAlpha(0);
    container.setDepth(20);

    // Card background
    const bg = this.add.rectangle(0, 0, w, h, 0x16163a);
    bg.setStrokeStyle(2, 0x3a3a88);
    container.add(bg);

    // Glow border shown only on hover
    const glow = this.add.rectangle(0, 0, w + 8, h + 8, 0x000000, 0);
    glow.setStrokeStyle(3, 0x44aaff, 0);
    container.add(glow);

    // Large icon
    const icon = this.add.text(0, -h / 2 + 56, upgrade.icon, {
      fontSize: '42px',
    }).setOrigin(0.5);
    container.add(icon);

    // Upgrade name
    const nameText = this.add.text(0, -h / 2 + 118, upgrade.name, {
      fontSize: '19px',
      color: '#ffffff',
      fontStyle: 'bold',
      wordWrap: { width: w - 24 },
      align: 'center',
    }).setOrigin(0.5);
    container.add(nameText);

    // Thin separator
    const sep = this.add.rectangle(0, -h / 2 + 148, w - 44, 1, 0x3a3a88);
    container.add(sep);

    // Description
    const desc = this.add.text(0, -h / 2 + 198, upgrade.description, {
      fontSize: '15px',
      color: '#9999cc',
      wordWrap: { width: w - 28 },
      align: 'center',
    }).setOrigin(0.5);
    container.add(desc);

    // Click hint at the bottom
    const hint = this.add.text(0, h / 2 - 28, 'click to select', {
      fontSize: '12px',
      color: '#44446a',
      fontStyle: 'italic',
    }).setOrigin(0.5);
    container.add(hint);

    // Make the background interactive
    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setFillStyle(0x22225e);
      glow.setStrokeStyle(3, 0x44aaff, 1);
      hint.setColor('#8888bb');
      this.tweens.add({ targets: container, scaleX: 1.04, scaleY: 1.04, duration: 90, ease: 'Power1' });
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x16163a);
      glow.setStrokeStyle(3, 0x44aaff, 0);
      hint.setColor('#44446a');
      this.tweens.add({ targets: container, scaleX: 1.0, scaleY: 1.0, duration: 90, ease: 'Power1' });
    });

    bg.on('pointerdown', () => {
      this.selectUpgrade(upgrade);
    });

    // Staggered entry animation
    this.tweens.add({
      targets: container,
      alpha: 1,
      y: cy,
      duration: 280,
      delay: index * 80,
      ease: 'Back.Out',
    });
  }

  private selectUpgrade(upgrade: Upgrade) {
    // Delegate application to GameScene, then resume and stop self
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gameScene = this.scene.get('GameScene') as any;
    if (gameScene && typeof gameScene.applyUpgrade === 'function') {
      gameScene.applyUpgrade(upgrade);
    }
    this.scene.resume('GameScene');
    this.scene.stop('UpgradeScene');
  }
}
